import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Sparkles, X, Send, AlertTriangle, Copy, Check, HelpCircle } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/useStore';
import '../styles/Assistenza.css';

/**
 * Assistenza — pannello laterale.
 *
 * SI PUO' SEMPLICEMENTE CHIEDERE UNA COSA
 * Fino al 4 settembre 2026 bisognava prima scegliere un compito fra
 * quelli abilitati, e i compiti erano tutti specialistici: abbinare
 * fatture fornitore, proporre aliquote, estrarre valori da un referto.
 * «Come creo una fattura?» non aveva nessun riquadro sotto cui stare, ed
 * e' la domanda che allo sportello viene ogni giorno.
 *
 * Adesso il compito predefinito e' «Come si usa il gestionale», e le
 * risposte vengono dai manuali: si apre il pannello, si scrive, e si ha
 * una risposta. I riquadri restano tutti ma sono scelte, non un
 * passaggio obbligato.
 *
 * I CONSIGLI SONO DOMANDE GIA' SCRITTE
 * Chi non sa cosa si puo' chiedere non chiede. Le domande suggerite
 * dipendono dal ruolo - a chi sta in cassa non serve sapere come si
 * compila un'attestazione di esenzione - e cliccandone una la si scrive
 * nel campo, senza mandarla: chi vuole la corregge prima.
 *
 * L'avviso sui dati del paziente resta sui compiti di consultazione,
 * dove il medico scrive liberamente.
 */

const COMPITO_PREDEFINITO = 'uso_gestionale';

/* Domande suggerite, per ruolo. Sono scritte come le direbbe una
   persona, non come un titolo di capitolo: e' quello che poi finisce nel
   campo di testo. */
const CONSIGLI = {
    segreteria: [
        'Come creo una fattura?',
        'Come correggo una fattura già emessa?',
        'Come accetto un paziente nuovo?',
        'Un paziente di San Marino: cosa cambia?',
        'Come registro il lotto di una tossina?',
        'Come mando un documento da firmare al tablet?',
        'Come creo l\'accesso a una collega nuova?',
        'Cosa devo guardare nelle trasmissioni al Sistema TS?',
    ],
    medico: [
        'Come registro una procedura?',
        'Come vedo una procedura riservata di un collega?',
        'Come compilo un\'attestazione di esenzione IVA?',
        'Dove metto parto, lavoro e famiglia di un paziente?',
        'Cosa vuol dire la somma dei codici di un questionario?',
        'Chi può modificare una voce di anamnesi?',
    ],
    admin: [
        'Come ripristino una modifica sbagliata?',
        'Chi può creare utenti, e cosa non può fare la segreteria?',
        'Dove si cambiano i dati della struttura sulle fatture?',
        'Come creo una fattura?',
        'Cosa succede ogni notte in automatico?',
        'Un appuntamento annullato dove finisce?',
    ],
};

const consigliPer = (ruolo) => CONSIGLI[ruolo] || CONSIGLI[
    ruolo === 'medico_senior' ? 'medico' : 'segreteria'
] || [];

/* `apertura`: un numero che cresce a ogni richiesta di aprire il pannello da
   fuori - il menu del tondo della testata del telefono (components/guscio/
   Telefono.jsx), dove il pulsante tondo e' nascosto perche' coprirebbe la
   barra in basso. Senza (il guscio largo, lo stile di prima) non cambia
   niente. */
const Assistenza = ({ apertura = 0 }) => {
    const utente = useAuthStore((state) => state.user);
    const [aperto, setAperto] = useState(false);
    useEffect(() => { if (apertura > 0) setAperto(true); }, [apertura]);
    const [stato, setStato] = useState(null);
    const [compito, setCompito] = useState(COMPITO_PREDEFINITO);
    /* Il paziente della scheda aperta (proposta 20, 29/09/2026): con lui il
       server sa quali dati togliere dal testo prima di mandarlo, e fa
       rispettare l'opposizione. Solo l'id: i dati li legge il server. */
    const posizione = useLocation();
    const pazienteAperto = Number((/^\/pazienti\/(\d+)/.exec(posizione.pathname) || [])[1]) || null;
    const [testo, setTesto] = useState('');
    const [scambi, setScambi] = useState([]);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [copiato, setCopiato] = useState(null);

    const fondo = useRef(null);

    useEffect(() => {
        if (!aperto || stato) return;
        (async () => {
            try {
                const r = await api.get('/ai/stato');
                setStato(r.data.data);
                const c = r.data.data.compiti || [];
                /* Si parte da «come si usa il gestionale» se c'e': e' la
                   domanda piu' probabile. Se per qualche ragione non e'
                   abilitato, si ripiega sul primo disponibile invece di
                   lasciare il pannello senza compito, che darebbe un
                   errore al primo invio. */
                if (!c.some((x) => x.codice === COMPITO_PREDEFINITO)) {
                    if (c.length) setCompito(c[0].codice);
                }
            } catch (e) {
                setErrore('Impossibile contattare il servizio');
            }
        })();
    }, [aperto, stato]);

    useEffect(() => {
        fondo.current?.scrollIntoView({ behavior: 'smooth' });
    }, [scambi, inCorso]);

    const invia = async () => {
        const domanda = testo.trim();
        if (!domanda || inCorso) return;

        setErrore(null);
        setInCorso(true);
        setTesto('');
        setScambi((s) => [...s, { chi: 'io', testo: domanda }]);

        try {
            const clinico = (stato?.compiti || []).find((c) => c.codice === compito)?.tratta_pazienti;
            const r = await api.post('/ai/chiedi', {
                compito, testo: domanda,
                ...(clinico && pazienteAperto ? { riferimento: { tipo: 'paziente', id: pazienteAperto } } : {}),
            });
            setScambi((s) => [...s, {
                chi: 'ai',
                testo: r.data.data.risposta,
                costo: r.data.data.costo,
                addebitato: r.data.data.addebitato,
            }]);
        } catch (e) {
            const m = e?.response?.data?.message || 'Errore nella richiesta';
            setScambi((s) => [...s, { chi: 'errore', testo: m }]);
            /* Il testo torna nel campo: se il filtro l'ha fermato, va
               corretto, non riscritto da capo. */
            setTesto((t) => t || domanda);
        } finally {
            setInCorso(false);
        }
    };

    const copia = async (t, i) => {
        try {
            await navigator.clipboard.writeText(t);
            setCopiato(i);
            setTimeout(() => setCopiato(null), 1800);
        } catch (e) { /* niente da fare se il browser non lo permette */ }
    };

    const sceltoOra = (stato?.compiti || []).find((c) => c.codice === compito);
    const consigli = consigliPer(utente?.ruolo);

    /* Un consiglio si scrive nel campo e non si manda: chi vuole lo
       corregge, e chi si e' sbagliato non ha speso una richiesta. */
    const usaConsiglio = (domanda) => {
        setCompito(COMPITO_PREDEFINITO);
        setTesto(domanda);
    };

    /* I compiti dove si scrive a mano: li' l'avviso serve davvero. */
    const aTestoLibero = ['consultazione_clinica', 'materiale_informativo'];

    if (!aperto) {
        return (
            <button className="asi-pulsante" onClick={() => setAperto(true)}
                    title="Assistenza">
                <Sparkles size={24} strokeWidth={1.8} />
            </button>
        );
    }

    return (
        <>
            <div className="asi-sfondo" onClick={() => setAperto(false)} />
            <aside className="asi-pannello">
                <header className="asi-testata">
                    <span><Sparkles size={17} strokeWidth={1.8} /> Assistenza</span>
                    <button onClick={() => setAperto(false)} aria-label="Chiudi">
                        <X size={18} />
                    </button>
                </header>

                {!stato ? (
                    <div className="asi-stato">Caricamento…</div>
                ) : !stato.attiva ? (
                    <div className="asi-stato">
                        <AlertTriangle size={22} />
                        <p>{stato.motivo || 'Assistenza non disponibile'}</p>
                    </div>
                ) : stato.compiti.length === 0 ? (
                    <div className="asi-stato">
                        <p>Nessun compito disponibile per il tuo ruolo.</p>
                    </div>
                ) : (
                    <>
                        <div className="asi-compiti">
                            {stato.compiti.map((c) => (
                                <button key={c.codice}
                                        className={compito === c.codice ? 'attivo' : ''}
                                        onClick={() => setCompito(c.codice)}>
                                    {c.nome}
                                </button>
                            ))}
                        </div>

                        {sceltoOra?.descrizione && (
                            <p className="asi-descrizione">{sceltoOra.descrizione}</p>
                        )}

                        {(aTestoLibero.includes(compito) || sceltoOra?.tratta_pazienti) && (
                            <div className="asi-avvertenza">
                                <AlertTriangle size={15} />
                                <span>
                                    Non inserire dati che identificano un paziente:
                                    nome, codice fiscale, recapiti. Descrivi il caso
                                    senza riferimenti alla persona.
                                    {sceltoOra?.tratta_pazienti && (pazienteAperto
                                        ? ' Dalla scheda di questo paziente, i suoi dati vengono comunque tolti prima dell\'invio.'
                                        : ' Fuori dalla scheda di un paziente vengono tolti solo codici fiscali, telefoni e indirizzi di posta.')}
                                </span>
                            </div>
                        )}

                        <div className="asi-scambi">
                            {scambi.length === 0 && (
                                <div className="asi-vuoto">
                                    {compito === 'consultazione_clinica' ? (
                                        'Chiedi di linee guida, soglie, confronto fra opzioni terapeutiche.'
                                    ) : compito === COMPITO_PREDEFINITO && consigli.length ? (
                                        <>
                                            <p className="asi-vuoto-titolo">
                                                <HelpCircle size={15} aria-hidden="true" />
                                                Chiedi come si fa una cosa
                                            </p>
                                            <p className="asi-vuoto-nota">
                                                Le risposte vengono dal manuale della tua
                                                area. Scrivi la tua domanda, oppure parti
                                                da una di queste.
                                            </p>
                                            <div className="asi-consigli">
                                                {consigli.map((d) => (
                                                    <button key={d} type="button"
                                                            onClick={() => usaConsiglio(d)}>
                                                        {d}
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        'Scrivi cosa ti serve.'
                                    )}
                                </div>
                            )}

                            {scambi.map((s, i) => (
                                <div key={i} className={`asi-scambio asi-${s.chi}`}>
                                    <div className="asi-testo">{s.testo}</div>
                                    {s.chi === 'ai' && (
                                        <div className="asi-piede">
                                            <button onClick={() => copia(s.testo, i)}>
                                                {copiato === i
                                                    ? <><Check size={13} /> copiato</>
                                                    : <><Copy size={13} /> copia</>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {inCorso && <div className="asi-attesa">Sto pensando…</div>}
                            <div ref={fondo} />
                        </div>

                        {errore && <div className="asi-errore">{errore}</div>}

                        <div className="asi-invio">
                            <textarea
                                value={testo}
                                onChange={(e) => setTesto(e.target.value)}
                                onKeyDown={(e) => {
                                    // Invio manda, Maiusc+Invio va a capo
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        invia();
                                    }
                                }}
                                placeholder="Scrivi qui…"
                                rows={2}
                                disabled={inCorso}
                            />
                            <button onClick={invia} disabled={inCorso || !testo.trim()}
                                    aria-label="Invia">
                                <Send size={17} />
                            </button>
                        </div>

                        {stato.consumo && (
                            <div className="asi-consumo">
                                Questo mese: {stato.consumo.chiamate} richieste ·{' '}
                                {stato.consumo.euro.toFixed(2).replace('.', ',')} €
                            </div>
                        )}
                    </>
                )}
            </aside>
        </>
    );
};

export default Assistenza;
